import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { AxiosError } from 'axios';
import { createBusiness, startEnrolation, validateCredentials } from 'lib/api';
import { getRutificator } from 'lib/api/business';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import ErrorHelper from 'lib/designSystem/ErrorHelper';
import { handleError } from 'lib/functions/errors';
import { showAlert, validaRut } from 'lib/functions/utils';
import React, { useState } from 'react';

function ConnectBusiness(props: any) {
  const {
    setLoading,
    dispatch,
    setOpenEditDialog,
    fetchRelations,
    openAddDialog,
    setOpenAddDialog,
  } = props;

  const [rutInput, setRutInput] = useState('');
  const [rutSearched, setRutSearched] = useState(false);
  const [razonSocialInput, setRazonSocialInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [connectSII, setConnectSII] = useState(false);
  const [loadingRut, setLoadingRut] = useState(false);
  const [rutError, setRutError] = useState('');
  const [razonSocialError, setRazonSocialError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showRutNotFound, setShowRutNotFound] = useState(false);

  const handleBuscarRut = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setRutError('');
    setRazonSocialError('');
    setPasswordError('');
    setShowRutNotFound(false);
    const rutValido = validaRut(rutInput);
    if (!rutValido) {
      setRutError('El RUT ingresado no es válido');
      return;
    }
    try {
      setLoadingRut(true);
      const data = await getRutificator(dispatch, [rutInput]);
      let razonSocial = '';
      if (data?.results?.[rutInput]?.[0]?.RAZON_SOCIAL) {
        razonSocial = data.results[rutInput][0].RAZON_SOCIAL.toUpperCase();
      }
      setRazonSocialInput(razonSocial);
      setRutSearched(true);
      if (!razonSocial) {
        setShowRutNotFound(true);
      }
    } catch (error: any) {
      setShowRutNotFound(true);
      handleError(error);
    }
    setLoadingRut(false);
  };

  // Handler unificado para crear negocio con o sin SII
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setRutError('');
      setRazonSocialError('');
      setPasswordError('');
      let valid = true;
      const rutValido = validaRut(rutInput);
      if (!rutValido) {
        setRutError('El RUT ingresado no es válido');
        valid = false;
      }
      if (!razonSocialInput) {
        setRazonSocialError('La razón social es requerida');
        valid = false;
      }
      if (!rutInput) {
        setRutError('El RUT es requerido');
        valid = false;
      }
      if (connectSII && !passwordInput) {
        setPasswordError('La contraseña es requerida');
        valid = false;
      }
      if (!valid) {
        setLoading(false);
        return;
      }
      if (connectSII) {
        await validateCredentials(dispatch, {
          taxPassword: passwordInput,
          taxIdentifier: rutInput,
        });
        const response = await createBusiness(dispatch, {
          name: razonSocialInput,
          identifier: rutInput,
          taxPassword: passwordInput,
          enrolledPath: 'myAccounts',
        });
        await startEnrolation(dispatch, {
          taxPassword: passwordInput,
          taxIdentifier: rutInput,
          businessId: response.id,
        });
        await fetchRelations();
        showAlert(
          dispatch,
          'success',
          'Estamos ingestando las nuevas facturas. Estas estarán disponibles en algunos minutos',
        );
      } else {
        await createBusiness(dispatch, {
          name: razonSocialInput,
          identifier: rutInput,
          taxPassword: passwordInput,
          enrolledPath: 'myAccounts',
        });
        await fetchRelations();
        showAlert(
          dispatch,
          'success',
          'Se ha creado un nuevo Cliente. Puedes activarla cuando lo necesites apretando "Actualizar Cliente"',
        );
      }
      handleClose();
    } catch (error: unknown) {
      const axiosMessage =
        error instanceof Error && 'response' in error
          ? (error as AxiosError<{ displayMessage?: string }>).response?.data
              ?.displayMessage
          : undefined;
      const defaultMessage = connectSII
        ? 'Error al conectar el cliente'
        : 'Error al crear el cliente';
      showAlert(dispatch, 'error', axiosMessage ?? defaultMessage);
      handleError(error);
    }
    setLoading(false);
  };

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRutInput(e.target.value.toUpperCase());
    setRutSearched(false);
    setRazonSocialInput('');
    setRutError('');
    setRazonSocialError('');
    setPasswordError('');
    setShowRutNotFound(false);
  };
  const handleRazonSocialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRazonSocialInput(e.target.value.toUpperCase());
    setRazonSocialError('');
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordInput(e.target.value);
    setPasswordError('');
  };
  const handleConnectSIIChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConnectSII(e.target.checked);
  };

  const handleClose = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setRutInput('');
    setRutSearched(false);
    setRazonSocialInput('');
    setPasswordInput('');
    setConnectSII(false);
    setLoadingRut(false);
  };

  const isFormValid = () => {
    if (!rutInput || !validaRut(rutInput) || rutError) return false;
    if (!razonSocialInput || razonSocialError) return false;
    if (connectSII && (!passwordInput || passwordError)) return false;
    return true;
  };

  return (
    <Dialog open={openAddDialog} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
        Conectar Cliente
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection={rutSearched ? 'column' : 'row'}
          gap={2}
          mt={1}
        >
          {/* Step 1: Only RUT input and search button */}
          <TextField
            margin="dense"
            name="identifier"
            label="RUT (sin puntos, con guión)"
            fullWidth
            value={rutInput}
            onChange={handleRutChange}
            disabled={rutSearched || loadingRut}
            error={!!rutError}
            helperText={<ErrorHelper message={rutError} />}
            autoFocus
          />
          {!rutSearched && (
            <Box display="flex" alignItems="center" gap={2}>
              <CustomButton
                onClick={handleBuscarRut}
                color="primary"
                disabled={loadingRut || !rutInput}
              >
                {loadingRut ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  <SearchIcon />
                )}
              </CustomButton>
            </Box>
          )}
          {/* Paso 2: Si ya se buscó el RUT, mostrar el resto del formulario */}
          {rutSearched && (
            <>
              <TextField
                margin="dense"
                name="businessName"
                label="Razón Social"
                fullWidth
                value={razonSocialInput}
                onChange={handleRazonSocialChange}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                required
                autoFocus
                error={!!razonSocialError}
                helperText={<ErrorHelper message={razonSocialError} />}
              />
              {connectSII && (
                <TextField
                  margin="dense"
                  name="password"
                  label="Contraseña"
                  value={passwordInput}
                  onChange={handlePasswordChange}
                  type="password"
                  fullWidth
                  required
                  error={!!passwordError}
                  helperText={<ErrorHelper message={passwordError} />}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={connectSII}
                    onChange={handleConnectSIIChange}
                    name="connectSII"
                    color="primary"
                  />
                }
                label="Conectar con SII"
              />
              {showRutNotFound && (
                <AlertBanner
                  severity="warning"
                  title="Error"
                  message="No se encontró razón social para este RUT. Puedes ingresarla manualmente."
                  showTitle={false}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <CustomButton
          onClick={handleClose}
          color="secondary"
          variant="outlined"
        >
          Cancelar
        </CustomButton>
        {rutSearched && (
          <CustomButton
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loadingRut || !isFormValid()}
          >
            Agregar
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ConnectBusiness;
